import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { EntitySwitcher } from "@/components/EntitySwitcher";
import { useEntity } from "@/hooks/useEntity";
import { useTenant } from "@/hooks/useTenant";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Filter, FileText, Loader2, Receipt } from "lucide-react";
import { useState } from "react";

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(Math.abs(value));
};

export default function Transactions() {
  const { orgId, familyId } = useTenant();
  const { entities } = useEntity();
  const [entityId, setEntityId] = useState<string | null>(null);

  const selectedEntity = entities.find(e => e.id === entityId);

  const { data: transactions, isLoading } = useQuery({
    queryKey: ['transactions', orgId, familyId, entityId],
    queryFn: async () => {
      if (!orgId) return [];

      const query = supabase
        .from('transactions')
        .select('*')
        .order('date', { ascending: false })
        .limit(100);

      // Note: transactions table may need org_id/family_id columns added
      // For now, fetch all and filter client-side if needed

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: !!orgId
  });

  const classColor = (txClass: string) => {
    const cls = (txClass || '').toLowerCase();
    if (cls.includes('contribution') || cls.includes('call')) return "bg-primary text-primary-foreground";
    if (cls.includes('distribution') || cls.includes('withdrawal')) return "bg-accent text-accent-foreground";
    if (cls.includes('fee')) return "bg-destructive text-destructive-foreground";
    if (cls.includes('income') || cls.includes('dividend')) return "bg-success text-success-foreground";
    if (cls.includes('trade')) return "bg-secondary text-secondary-foreground";
    return "bg-muted text-muted-foreground";
  };

  const statusColor = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s === 'settled' || s === 'completed') return "bg-success text-success-foreground";
    if (s === 'pending') return "bg-warning text-warning-foreground";
    if (s === 'cancelled') return "bg-muted text-muted-foreground";
    return "bg-secondary text-secondary-foreground";
  };

  return (
    <div className="flex-1 space-y-4 px-6 py-6">

      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">Transactions</h1>
            {selectedEntity && (
              <Badge variant="secondary">{selectedEntity.name}</Badge>
            )}
          </div>
          <p className="text-muted-foreground">
            Complete transaction history across all accounts
          </p>
        </div>
        <div className="flex gap-2">
          <EntitySwitcher value={entityId} onValueChange={setEntityId} />
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4" />
            Filter
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading transactions...</span>
        </div>
      ) : transactions && transactions.length > 0 ? (
        <div className="rounded-md border overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Class</th>
                <th className="text-right">Amount</th>
                <th>Account</th>
                <th>Status</th>
                <th className="text-center">Doc</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => (
                <tr key={tx.id}>
                  <td className="text-muted-foreground">
                    {tx.date ? new Date(tx.date).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="max-w-md">{tx.description || tx.memo || 'No description'}</td>
                  <td>
                    <Badge className={classColor(tx.class || '')}>
                      {tx.class || 'Other'}
                    </Badge>
                  </td>
                  <td className={`text-right font-mono font-semibold ${Number(tx.amount) >= 0 ? "text-success" : "text-foreground"}`}>
                    {Number(tx.amount) >= 0 ? "+" : "-"}{formatCurrency(Number(tx.amount) || 0)}
                  </td>
                  <td>
                    <Badge variant="outline">{tx.account_id ? 'Account' : 'N/A'}</Badge>
                  </td>
                  <td>
                    <Badge variant="outline" className={statusColor(tx.status || '')}>
                      {tx.status || 'Unknown'}
                    </Badge>
                  </td>
                  <td className="text-center">
                    {tx.document_id && (
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <FileText className="h-4 w-4 text-primary" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Receipt className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Transactions</h3>
          <p className="text-muted-foreground max-w-sm">
            Transaction data will appear here once statements are uploaded and processed.
          </p>
        </div>
      )}
    </div>
  );
}
